import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled, { ThemeProvider } from 'styled-components';
import darkTheme from 'themes/darkTheme';
import ButtonV2 from 'components/ButtonV2';
import Card from 'components/Card';

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const ContentWrapper = styled.div`
  max-width: 700px;
  width: 100%;
`;

const StripeIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #635bff 0%, #8b84ff 100%);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  box-shadow: 0 8px 24px rgba(99, 91, 255, 0.3);
`;

const StripeLogo = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: #ffffff;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #ffffff;
  text-align: center;
  margin: 0 0 12px 0;
`;

const Subtitle = styled.p`
  font-size: 15px;
  color: #94a3b8;
  text-align: center;
  line-height: 1.6;
  margin: 0 0 32px 0;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 20px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const FeatureIcon = styled.div`
  width: 32px;
  height: 32px;
  min-width: 32px;
  background: rgba(250, 70, 22, 0.15);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 16px;
    height: 16px;
    color: #fa4616;
  }
`;

const FeatureContent = styled.div`
  flex: 1;
`;

const FeatureTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 4px 0;
`;

const FeatureDescription = styled.p`
  font-size: 13px;
  color: #94a3b8;
  margin: 0;
  line-height: 1.5;
`;

const InfoBox = styled.div`
  background: rgba(99, 91, 255, 0.1);
  border: 1px solid rgba(99, 91, 255, 0.2);
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 32px;
`;

const InfoText = styled.p`
  font-size: 14px;
  color: #94a3b8;
  line-height: 1.6;
  margin: 0;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  button {
    width: 100%;
  }
`;

const SecondaryButton = styled.button`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: #94a3b8;
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: rgba(255, 255, 255, 0.3);
    color: #ffffff;
  }
`;

const StripeSetupStep = () => {
  const history = useHistory();
  const [isConnecting, setIsConnecting] = useState(false);

  const handleConnectStripe = async () => {
    setIsConnecting(true);

    try {
      // TODO: Implement actual Stripe Connect flow
      // This would typically redirect to Stripe's OAuth flow
      // For now, show a placeholder message
      await new Promise((resolve) => setTimeout(resolve, 1500));
      toast.info('Stripe Connect integration coming soon!');
      setIsConnecting(false);
    } catch (error) {
      toast.error('Failed to initiate Stripe connection. Please try again.');
      setIsConnecting(false);
    }
  };

  const handleSkipForNow = () => {
    // Navigate to dashboard or login
    history.push('/login');
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <PageContainer>
        <ContentWrapper>
          <Card>
            <StripeIcon>
              <StripeLogo>S</StripeLogo>
            </StripeIcon>

            <Title>Connect Your Payment Account</Title>

            <Subtitle>
              Set up Stripe to accept payments from your customers. This secure
              connection allows you to process transactions directly.
            </Subtitle>

            <FeatureGrid>
              <FeatureCard>
                <FeatureIcon>
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                </FeatureIcon>
                <FeatureContent>
                  <FeatureTitle>Secure Payments</FeatureTitle>
                  <FeatureDescription>
                    Bank-level security with PCI compliance
                  </FeatureDescription>
                </FeatureContent>
              </FeatureCard>

              <FeatureCard>
                <FeatureIcon>
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </FeatureIcon>
                <FeatureContent>
                  <FeatureTitle>Fast Deposits</FeatureTitle>
                  <FeatureDescription>
                    Get paid quickly with automatic transfers
                  </FeatureDescription>
                </FeatureContent>
              </FeatureCard>

              <FeatureCard>
                <FeatureIcon>
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                </FeatureIcon>
                <FeatureContent>
                  <FeatureTitle>Detailed Reports</FeatureTitle>
                  <FeatureDescription>
                    Track all your transactions and revenue
                  </FeatureDescription>
                </FeatureContent>
              </FeatureCard>

              <FeatureCard>
                <FeatureIcon>
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                    />
                  </svg>
                </FeatureIcon>
                <FeatureContent>
                  <FeatureTitle>Multiple Payment Methods</FeatureTitle>
                  <FeatureDescription>
                    Accept cards, Apple Pay, Google Pay, and more
                  </FeatureDescription>
                </FeatureContent>
              </FeatureCard>
            </FeatureGrid>

            <InfoBox>
              <InfoText>
                <strong>What you'll need:</strong> You'll be redirected to
                Stripe to complete your account setup. Have your business
                details, bank account information, and tax ID ready. The process
                typically takes 5-10 minutes.
              </InfoText>
            </InfoBox>

            <ButtonContainer>
              <ButtonV2 onClick={handleConnectStripe} disabled={isConnecting}>
                {isConnecting ? 'Connecting...' : 'Connect with Stripe →'}
              </ButtonV2>
              <SecondaryButton onClick={handleSkipForNow}>
                I'll set this up later
              </SecondaryButton>
            </ButtonContainer>
          </Card>
        </ContentWrapper>
      </PageContainer>
    </ThemeProvider>
  );
};

export default StripeSetupStep;
